import React, {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {IAmplitudeOfferPromotionTrackingParamsWithItemId} from "../tracking/get_amplitude_offer_promotion_event_properties";
import {getAmplitudePromotionsLeadFormTrackingContext} from "../tracking/get_amplitude_promotions_lead_form_tracking_context";
import {useOfferPageAmplitudeItemListTrackingContext} from "../tracking/OfferPageAmplitudeItemListTrackingContext";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";
import {PromotionDetail} from "../types/PromotionDetail";
import {Promotion} from "./Promotion";

interface IProps {
    promotions?: PromotionDetail[];
    vendorId: number;
    offerName: string;
    offerId?: number;
    propertyId?: number;
    isAutoresponderEnabled: boolean;
    isSaleOverLimit?: boolean;
    sectionStyle?: string;
    ref?: React.Ref<HTMLElement>;
    variant?: "column" | "row";
}

export const Promotions = (props: IProps) => {
    const {promotions, sectionStyle, ref, variant, vendorId, offerName, offerId, propertyId} = props;
    const {isMobile} = useUserDevice();
    const {itemListContext, isResolved: isItemListContextResolved} = useOfferPageAmplitudeItemListTrackingContext();
    const promotionItems = promotions ?? [];

    const isRowVariant = variant === "row";
    const offerListCols = getOfferListCols(promotionItems.length);

    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;

    const gtmData: IGTMEventLeadForm = {
        clickText: PROMOTIONS_SECTION_CTA,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudePromotionsLeadFormTrackingContext({
            promotions: promotionItems,
            offerId,
            propertyId,
            applicationSource,
            ctaText: PROMOTIONS_SECTION_CTA
        });
    }, [promotionItems, offerId, propertyId, applicationSource]);

    const selectPromotionTrackingContext = useMemo((): IAmplitudeOfferPromotionTrackingParamsWithItemId | null => {
        if (!offerId || promotionItems.length === 0) {
            return null;
        }

        const contexts = promotionItems.map((promotion) =>
            getAmplitudeOfferPagePromotionTrackingContext({
                promotion,
                offerId,
                propertyId,
                itemListId: itemListContext?.itemListId,
                creativeSlot: "promotion_box"
            })
        );

        return {
            ...contexts[0],
            creativeName: contexts.flatMap((context) => context.creativeName),
            promotionId: contexts.flatMap((context) => context.promotionId ?? 0),
            promotionName: contexts.flatMap((context) => context.promotionName),
            promotionContent: contexts.flatMap((context) => context.promotionContent ?? "brak opisu")
        };
    }, [promotionItems, offerId, propertyId, itemListContext?.itemListId]);

    if (promotionItems.length === 0) {
        return null;
    }

    const handleButtonClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (selectPromotionTrackingContext) {
            trackAmplitudeOfferSelectPromotion(selectPromotionTrackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    return (
        <section id="promocje" ref={ref} className={cx(promotionsSectionStyle, sectionStyle)}>
            <div className={promotionsGridStyle}>
                <div className={promotionsIconStyle}>
                    <PercentIcon size="3.2" wrapperSize={isMobile ? "4.4" : "5.6"} wrapperColor="var(--colors-highlight)" />
                </div>

                <Text as="h2" variant="headline_4" className={promotionsHeaderStyle}>
                    Promocje
                </Text>

                <div className={cx(promotionsContentStyle, isRowVariant && promotionsContentOfferStyle)}>
                    <ul
                        className={cx(
                            promotionsListStyle,
                            isRowVariant && promotionsListOfferStyle,
                            isRowVariant && promotionsListOfferColStyles[offerListCols]
                        )}
                    >
                        {promotionItems.map((promotion) => (
                            <Promotion
                                key={promotion.id}
                                promotion={promotion}
                                offerId={offerId}
                                propertyId={propertyId}
                                itemListId={itemListContext?.itemListId}
                                isItemListContextResolved={isItemListContextResolved}
                            />
                        ))}
                    </ul>

                    <div className={cx(promotionsButtonAreaStyle, isRowVariant && promotionsButtonOfferStyle)}>
                        <ApplicationModalButton
                            flowVariant="promotion"
                            initialStep={ApplicationModalStep.PROMOTION_FORM}
                            source={applicationSource}
                            sourceSection={ApplicationSourceSection.MODAL}
                            topic={{
                                message: `Dzień dobry, proszę o więcej informacji o promocjach w inwestycji ${offerName}.`
                            }}
                            vendorId={vendorId}
                            offerId={offerId || null}
                            propertyId={propertyId || null}
                            render={({openModal}) => (
                                <Button
                                    type="button"
                                    onClick={() => handleButtonClick(openModal)}
                                    variant="outlined_secondary"
                                    size={isMobile ? "small" : "medium"}
                                    className={css({minWidth: "20rem"})}
                                >
                                    {PROMOTIONS_SECTION_CTA}
                                </Button>
                            )}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const PROMOTIONS_SECTION_CTA = "ZAPYTAJ O PROMOCJE";

const getOfferListCols = (count: number): 1 | 2 | 3 => {
    if (count <= 3) {
        return count as 1 | 2 | 3;
    }
    if (count === 4) {
        return 2;
    }
    return 3;
};

const promotionsSectionStyle = css({
    p: 2,
    borderRadius: 2,
    border: "1px solid var(--colors-gray-600)",
    md: {
        p: 3
    }
});

const promotionsGridStyle = css({
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gridTemplateAreas: '"icon header" ". content"',
    columnGap: 2
});

const promotionsIconStyle = css({
    gridArea: "icon",
    alignSelf: "center"
});

const promotionsHeaderStyle = css({
    gridArea: "header",
    alignSelf: "center",
    m: 0,
    mb: 0
});

const promotionsContentStyle = css({
    gridArea: "content",
    mt: 2
});

const promotionsContentOfferStyle = css({
    md: {
        display: "flex",
        alignItems: "flex-start",
        gap: 3
    }
});

const promotionsListStyle = css({
    listStyle: "none",
    m: 0,
    p: 0
});

const promotionsListOfferStyle = css({
    md: {
        display: "grid",
        gap: 3,
        flex: 1
    }
});

const promotionsListOfferColStyles: Record<1 | 2 | 3, string> = {
    1: css({md: {gridTemplateColumns: "1fr"}}),
    2: css({md: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({md: {gridTemplateColumns: "repeat(3, 1fr)"}})
};

const promotionsButtonAreaStyle = css({
    mt: 2
});

const promotionsButtonOfferStyle = css({
    md: {
        mt: 0,
        alignSelf: "center",
        flexShrink: 0
    }
});
import React, {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {IAmplitudeOfferPromotionTrackingParamsWithItemId} from "../tracking/get_amplitude_offer_promotion_event_properties";
import {getAmplitudePromotionsLeadFormTrackingContext} from "../tracking/get_amplitude_promotions_lead_form_tracking_context";
import {useOfferPageAmplitudeItemListTrackingContext} from "../tracking/OfferPageAmplitudeItemListTrackingContext";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";
import {PromotionDetail} from "../types/PromotionDetail";
import {Promotion} from "./Promotion";

interface IProps {
    promotions?: PromotionDetail[];
    vendorId: number;
    offerName: string;
    offerId?: number;
    propertyId?: number;
    isAutoresponderEnabled: boolean;
    isSaleOverLimit?: boolean;
    sectionStyle?: string;
    ref?: React.Ref<HTMLElement>;
    variant?: "column" | "row";
}

export const Promotions = (props: IProps) => {
    const {promotions, sectionStyle, ref, variant, vendorId, offerName, offerId, propertyId} = props;
    const {isMobile} = useUserDevice();
    const {itemListContext, isResolved: isItemListContextResolved} = useOfferPageAmplitudeItemListTrackingContext();
    const promotionItems = promotions ?? [];

    const isRowVariant = variant === "row";
    const offerListCols = getOfferListCols(promotionItems.length);

    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;

    const gtmData: IGTMEventLeadForm = {
        clickText: PROMOTIONS_SECTION_CTA,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudePromotionsLeadFormTrackingContext({
            promotions: promotionItems,
            offerId,
            propertyId,
            applicationSource,
            ctaText: PROMOTIONS_SECTION_CTA
        });
    }, [promotionItems, offerId, propertyId, applicationSource]);

    const selectPromotionTrackingContext = useMemo((): IAmplitudeOfferPromotionTrackingParamsWithItemId | null => {
        if (!offerId || promotionItems.length === 0) {
            return null;
        }

        const contexts = promotionItems.map((promotion) =>
            getAmplitudeOfferPagePromotionTrackingContext({
                promotion,
                offerId,
                propertyId,
                itemListId: itemListContext?.itemListId,
                creativeSlot: "promotion_box"
            })
        );

        return {
            ...contexts[0],
            creativeName: contexts.flatMap((context) => context.creativeName),
            promotionId: contexts.flatMap((context) => context.promotionId ?? 0),
            promotionName: contexts.flatMap((context) => context.promotionName),
            promotionContent: contexts.flatMap((context) => context.promotionContent ?? "brak opisu")
        };
    }, [promotionItems, offerId, propertyId, itemListContext?.itemListId]);

    if (promotionItems.length === 0) {
        return null;
    }

    const handleButtonClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (selectPromotionTrackingContext) {
            trackAmplitudeOfferSelectPromotion(selectPromotionTrackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    return (
        <section id="promocje" ref={ref} className={cx(promotionsSectionStyle, sectionStyle)}>
            <div className={promotionsGridStyle}>
                <div className={promotionsIconStyle}>
                    <PercentIcon size="3.2" wrapperSize={isMobile ? "4.4" : "5.6"} wrapperColor="var(--colors-highlight)" />
                </div>

                <Text as="h2" variant="headline_4" className={promotionsHeaderStyle}>
                    Promocje
                </Text>

                <div className={cx(promotionsContentStyle, isRowVariant && promotionsContentOfferStyle)}>
                    <ul
                        className={cx(
                            promotionsListStyle,
                            isRowVariant && promotionsListOfferStyle,
                            isRowVariant && promotionsListOfferColStyles[offerListCols]
                        )}
                    >
                        {promotionItems.map((promotion) => (
                            <Promotion
                                key={promotion.id}
                                promotion={promotion}
                                offerId={offerId}
                                propertyId={propertyId}
                                itemListId={itemListContext?.itemListId}
                                isItemListContextResolved={isItemListContextResolved}
                            />
                        ))}
                    </ul>

                    <div className={cx(promotionsButtonAreaStyle, isRowVariant && promotionsButtonOfferStyle)}>
                        <ApplicationModalButton
                            flowVariant="promotion"
                            initialStep={ApplicationModalStep.PROMOTION_FORM}
                            source={applicationSource}
                            sourceSection={ApplicationSourceSection.MODAL}
                            topic={{
                                message: `Dzień dobry, proszę o więcej informacji o promocjach w inwestycji ${offerName}.`
                            }}
                            vendorId={vendorId}
                            offerId={offerId || null}
                            propertyId={propertyId || null}
                            render={({openModal}) => (
                                <Button
                                    type="button"
                                    onClick={() => handleButtonClick(openModal)}
                                    variant="outlined_secondary"
                                    size={isMobile ? "small" : "medium"}
                                    className={css({minWidth: "20rem"})}
                                >
                                    {PROMOTIONS_SECTION_CTA}
                                </Button>
                            )}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const PROMOTIONS_SECTION_CTA = "ZAPYTAJ O PROMOCJE";

const getOfferListCols = (count: number): 1 | 2 | 3 => {
    if (count <= 3) {
        return count as 1 | 2 | 3;
    }
    if (count === 4) {
        return 2;
    }
    return 3;
};

const promotionsSectionStyle = css({
    p: 2,
    borderRadius: 2,
    border: "1px solid var(--colors-gray-600)",
    md: {
        p: 3
    }
});

const promotionsGridStyle = css({
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gridTemplateAreas: '"icon header" ". content"',
    columnGap: 2
});

const promotionsIconStyle = css({
    gridArea: "icon",
    alignSelf: "center"
});

const promotionsHeaderStyle = css({
    gridArea: "header",
    alignSelf: "center",
    m: 0,
    mb: 0
});

const promotionsContentStyle = css({
    gridArea: "content",
    mt: 2
});

const promotionsContentOfferStyle = css({
    md: {
        display: "flex",
        alignItems: "flex-start",
        gap: 3
    }
});

const promotionsListStyle = css({
    listStyle: "none",
    m: 0,
    p: 0
});

const promotionsListOfferStyle = css({
    md: {
        display: "grid",
        gap: 3,
        flex: 1
    }
});

const promotionsListOfferColStyles: Record<1 | 2 | 3, string> = {
    1: css({md: {gridTemplateColumns: "1fr"}}),
    2: css({md: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({md: {gridTemplateColumns: "repeat(3, 1fr)"}})
};

const promotionsButtonAreaStyle = css({
    mt: 2
});

const promotionsButtonOfferStyle = css({
    md: {
        mt: 0,
        alignSelf: "center",
        flexShrink: 0
    }
});
